import React from 'react';
import { useQuery } from '@apollo/client';
import { Container, Typography, CircularProgress, Box, Paper } from '@mui/material';
import { GET_OUTGOING_ORDERS, GET_COMPANY_CONNECTIONS } from '../../../graphql/queries';
import OrdersTable from './OrdersTable';
import UnauthorizedErrorPage from '../../ErrorPages/UnauthorizedErrorPage';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import EmptyState from '../../../Components/EmptyState/EmptyState';

const MyOrders: React.FC = () => {
  const emptyImage = require('../../../assets/images/Empty1.png');

  const { data: connectionsData, loading: connectionsLoading } = useQuery(
    GET_COMPANY_CONNECTIONS
  );
  const hasConnections = Boolean(connectionsData?.companyConnections?.length);

  const { loading, error, data } = useQuery(GET_OUTGOING_ORDERS, {
    variables: {
      dateRange: {
        start: '2020-01-01',
        end: '2030-12-31',
      },
    },
    skip: !hasConnections,
  });

  if (connectionsLoading || (hasConnections && loading)) {
    return (
      <Container maxWidth="lg">
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress data-testid="loading-spinner" />
        </Box>
      </Container>
    );
  }

  if (!hasConnections) {
    return (
      <Container maxWidth="lg">
        <Box
          my={4}
          sx={{ width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
        >
          <Typography variant="h4" gutterBottom>
            Orders
          </Typography>
        </Box>
        <EmptyState
          image={emptyImage}
          text="Connect your Shopify store to see orders from your buyers here. Install the ShareStock app from your Shopify admin to get started."
          actionButtons={<></>}
        />
      </Container>
    );
  }

  if (error) {
    if (error.message.toLowerCase().includes("authorization")) {
      return <UnauthorizedErrorPage />;
    }
    return (
      <Container maxWidth="lg">
        <Box
          my={4}
          sx={{ width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
        >
          <Typography variant="h4" gutterBottom>
            Orders
          </Typography>
        </Box>
        <Paper sx={{ p: 3, textAlign: 'center' }}>
          <Typography color="error">Error: {error.message}</Typography>
        </Paper>
      </Container>
    );
  }

  const orders = data?.outgoingSales || [];

  return (
    <Container maxWidth="lg">
      <Box
        my={4}
        sx={{ width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
      >
        <Typography variant="h4" gutterBottom>
          Orders
        </Typography>
      </Box>
      {orders.length === 0 ? (
        <Paper sx={{ p: 6, textAlign: 'center' }}>
          <ShoppingCartIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h6" color="text.secondary" gutterBottom>
            No orders yet
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Orders from your connected Shopify store will appear here.
          </Typography>
        </Paper>
      ) : (
        <OrdersTable orders={orders} />
      )}
    </Container>
  );
};

export default MyOrders;
